import { api } from "./apiClient";

const DEFAULT_TIMEOUT_MS = 45000;

const AI_PATHS = {
  chat: "/ai/chat/",
  feedback: "/ai/feedback/",
  knowledgeDecision: "/ai/knowledge/decision/",
  health: "/ai/health/",
  quota: "/ai/quota/",
};

function normalizeRateLimit(value) {
  return value && typeof value === "object" ? value : null;
}

function normalizeChatAction(value) {
  if (!value || typeof value !== "object") return null;
  if (value.type !== "confirm_learning" || !value.proposal_id) return null;
  return {
    type: value.type,
    proposalId: String(value.proposal_id),
    content: typeof value.content === "string" ? value.content : "",
    expiresAt: value.expires_at || null,
  };
}

function getErrorData(error) {
  const data = error?.response?.data;
  return data && typeof data === "object" ? data : {};
}

function normalizeAiError(error, fallbackMessage, timeoutMessage) {
  if (error?.code === "ECONNABORTED") {
    return new Error(timeoutMessage || fallbackMessage);
  }

  const data = getErrorData(error);
  const normalized = new Error(
    data.error || data.detail || data.message || error?.message || fallbackMessage
  );
  normalized.status = error?.response?.status;
  normalized.code = data.code || error?.code;
  normalized.rateLimit = normalizeRateLimit(data.rate_limit);
  return normalized;
}

export async function sendChatMessage(message, history = [], options = {}) {
  const text = String(message || "").trim();
  if (!text) throw new Error("메시지를 입력해주세요.");

  try {
    const res = await api.post(
      AI_PATHS.chat,
      {
        message: text,
        history: Array.isArray(history) ? history : [],
        conversation_id: options.conversationId || null,
        page_path: options.pagePath || null,
      },
      {
        timeout: options.timeoutMs || DEFAULT_TIMEOUT_MS,
      }
    );
    const data = res.data || {};

    return {
      reply: typeof data.reply === "string" ? data.reply : "",
      history: Array.isArray(data.history) ? data.history : [],
      conversationId: typeof data.conversation_id === "string" ? data.conversation_id : null,
      responseId: typeof data.response_id === "string" ? data.response_id : null,
      rateLimit: normalizeRateLimit(data.rate_limit),
      usage: data.usage && typeof data.usage === "object" ? data.usage : null,
      action: normalizeChatAction(data.action),
    };
  } catch (error) {
    throw normalizeAiError(
      error,
      "AI 서버와 통신하지 못했습니다.",
      "AI 서버 응답 시간이 초과되었습니다."
    );
  }
}

export async function decideAiKnowledge(proposalId, decision, options = {}) {
  const normalizedDecision = String(decision || "").trim();
  if (!proposalId || !["confirm", "reject"].includes(normalizedDecision)) {
    throw new Error("학습 확인 값이 올바르지 않습니다.");
  }

  try {
    const res = await api.post(
      AI_PATHS.knowledgeDecision,
      {
        proposal_id: proposalId,
        decision: normalizedDecision,
      },
      {
        timeout: options.timeoutMs || 10000,
      }
    );
    return res.data || {};
  } catch (error) {
    throw normalizeAiError(
      error,
      "학습 확인에 실패했습니다.",
      "학습 확인 시간이 초과되었습니다."
    );
  }
}

export async function sendChatFeedback(feedback, options = {}) {
  const rating = String(feedback?.rating || "").trim();
  if (!["like", "dislike"].includes(rating)) {
    throw new Error("피드백 값이 올바르지 않습니다.");
  }

  try {
    const res = await api.post(
      AI_PATHS.feedback,
      {
        conversation_id: feedback.conversationId || null,
        response_id: feedback.responseId || null,
        rating,
        question: feedback.question || "",
        answer: feedback.answer || "",
      },
      {
        timeout: options.timeoutMs || 10000,
      }
    );

    return res.data;
  } catch (error) {
    throw normalizeAiError(
      error,
      "피드백 저장에 실패했습니다.",
      "피드백 저장 시간이 초과되었습니다."
    );
  }
}

export async function checkAiHealth(options = {}) {
  try {
    const res = await api.get(AI_PATHS.health, {
      params: { _: Date.now() },
      timeout: options.timeoutMs || 5000,
    });
    const data = res.data || {};

    return {
      ok: data.status === "ok",
      rateLimit: normalizeRateLimit(data.rate_limit),
    };
  } catch (error) {
    const data = getErrorData(error);
    return {
      ok: false,
      rateLimit: normalizeRateLimit(data.rate_limit),
    };
  }
}

export async function checkAiQuota(options = {}) {
  try {
    const res = await api.get(AI_PATHS.quota, {
      params: { _: Date.now() },
      timeout: options.timeoutMs || 10000,
    });
    const data = res.data || {};

    return {
      ok: Boolean(data.ok),
      rateLimit: normalizeRateLimit(data.rate_limit),
    };
  } catch (error) {
    throw normalizeAiError(
      error,
      "AI 한도 확인에 실패했습니다.",
      "AI 한도 확인 시간이 초과되었습니다."
    );
  }
}
